import React, { useState, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Upload, FileText, Check, Loader2, Eye, Trash2, BookUser, CreditCard, Fingerprint, Stamp, Car, Vote } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import CityCombobox from '@/components/forms/CityCombobox';
import { INDIAN_PASSPORT_CITIES } from '@/data/indianPassportCities';
import { extractOcrName, extractOcrAddress, extractOcrPincode } from '@/utils/ocrFields';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const INDIAN_CITIES = INDIAN_PASSPORT_CITIES;

const DOC_TYPES = [
  { value: 'passport_front', label: 'Passport (Front)', icon: BookUser },
  { value: 'passport_back', label: 'Passport (Back)', icon: BookUser },
  { value: 'pan', label: 'PAN Card', icon: CreditCard },
  { value: 'aadhar', label: 'Aadhar Card', icon: Fingerprint },
  { value: 'visa', label: 'Visa', icon: Stamp },
  { value: 'driving_license', label: 'Driving License', icon: Car },
  { value: 'voter_id', label: 'Voter ID', icon: Vote }
];

const DocUploadCard = ({ docType, label, icon: Icon, ucc, onExtracted, existingDocs }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);

  const existing = existingDocs.filter(d => d.doc_type === docType && !d.is_deleted);

  const handleUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File too large (max 10MB)');
      return;
    }

    setUploading(true);
    setPreview(URL.createObjectURL(file));

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('doc_type', docType);
      formData.append('run_ocr_extraction', 'true');

      const res = await axios.post(
        `${BACKEND_URL}/api/clients/${ucc}/documents/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      const { extracted_data, auto_filled_fields } = res.data;

      if (extracted_data && !extracted_data.error) {
        onExtracted(docType, extracted_data, auto_filled_fields);
        toast.success(`${label} uploaded & details extracted!`);
      } else {
        toast.success(`${label} uploaded (OCR could not extract — fill manually)`);
      }
    } catch (err) {
      toast.error(`Failed to upload ${label}`);
      setPreview(null);
    } finally {
      setUploading(false);
    }
  }, [ucc, docType, label, onExtracted]);

  return (
    <div
      className="border-2 border-dashed border-slate-200 rounded-2xl p-5 hover:border-teal-300 transition-colors relative group"
      data-testid={`doc-upload-${docType}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className="h-[18px] w-[18px] text-slate-500" strokeWidth={1.75} />
          <span className="font-semibold text-slate-800 text-sm">{label}</span>
        </div>
        {existing.length > 0 && (
          <span className="text-xs bg-teal-50 text-teal-700 px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
            <Check className="h-3 w-3" /> Uploaded
          </span>
        )}
      </div>

      {preview || existing.length > 0 ? (
        <div className="flex items-center gap-3 mb-3">
          {preview && (
            <img src={preview} alt="Preview" className="w-16 h-12 object-cover rounded-lg border" />
          )}
          {!preview && existing.length > 0 && (
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <FileText className="h-4 w-4" />
              <span>{existing[existing.length - 1].display_name || existing[existing.length - 1].original_filename}</span>
            </div>
          )}
        </div>
      ) : null}

      <label className="cursor-pointer">
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp,application/pdf"
          className="hidden"
          onChange={handleUpload}
          disabled={uploading}
          data-testid={`doc-input-${docType}`}
        />
        <div className="flex items-center justify-center gap-2 bg-slate-50 hover:bg-teal-50 rounded-xl py-2.5 px-4 transition-colors">
          {uploading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin text-teal-600" />
              <span className="text-xs text-teal-600 font-medium">Extracting details...</span>
            </>
          ) : (
            <>
              <Upload className="h-4 w-4 text-slate-500" />
              <span className="text-xs text-slate-600 font-medium">
                {existing.length > 0 ? 'Re-upload' : 'Upload Image'}
              </span>
            </>
          )}
        </div>
      </label>
    </div>
  );
};

export const DocumentsTab = ({ formData, handleChange, handleSelectChange, ucc, piiOnFile }) => {
  const [documents, setDocuments] = useState([]);
  const [loadedDocs, setLoadedDocs] = useState(false);
  const onFile = piiOnFile || {};
  const hiddenHint = '•••• on file — leave blank to keep';

  // Load existing documents
  React.useEffect(() => {
    if (ucc && !loadedDocs) {
      axios.get(`${BACKEND_URL}/api/clients/${ucc}/documents`)
        .then(res => {
          setDocuments(res.data.documents || []);
          setLoadedDocs(true);
        })
        .catch(() => setLoadedDocs(true));
    }
  }, [ucc, loadedDocs]);

  const handleOcrExtracted = useCallback((docType, extractedData, autoFilled) => {
    // Auto-fill form fields from OCR
    if (autoFilled) {
      Object.entries(autoFilled).forEach(([field, value]) => {
        if (value) {
          handleChange({ target: { name: field, value } });
        }
      });
    }

    // Also fill from extracted data for fields not auto-saved
    if (extractedData) {
      const ocrName = extractOcrName(extractedData);
      if (ocrName && !formData.fullName) {
        handleChange({ target: { name: 'fullName', value: ocrName } });
      }
      if (extractedData.dateOfBirth && !formData.dateOfBirth) {
        handleChange({ target: { name: 'dateOfBirth', value: extractedData.dateOfBirth } });
      }
      if (extractedData.gender && !formData.gender) {
        handleSelectChange('gender', extractedData.gender);
      }
      const ocrAddress = extractOcrAddress(extractedData);
      if (ocrAddress && !formData.address) {
        handleChange({ target: { name: 'address', value: ocrAddress } });
      }
      const ocrPincode = extractOcrPincode(extractedData);
      if (ocrPincode && !formData.pincode) {
        handleChange({ target: { name: 'pincode', value: ocrPincode } });
      }
    }

    // Refresh docs list
    if (ucc) {
      axios.get(`${BACKEND_URL}/api/clients/${ucc}/documents`)
        .then(res => setDocuments(res.data.documents || []))
        .catch(() => {});
    }
  }, [formData.fullName, formData.dateOfBirth, formData.gender, formData.address, formData.pincode, handleChange, handleSelectChange, ucc]);

  return (
    <Card className="p-6 sm:p-10 bg-white/80 backdrop-blur-xl border-2 border-slate-100 rounded-3xl shadow-xl">
      <h2 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900 mb-3" data-testid="documents-section-title">
        Travel Documents
      </h2>
      <p className="text-sm text-slate-500 mb-8">Upload document images to auto-extract details via AI, or fill manually below.</p>

      {/* Upload Section */}
      {ucc && (
        <div className="mb-10">
          <h3 className="text-sm font-semibold text-slate-700 mb-4 uppercase tracking-wider">Upload & Auto-Extract</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {DOC_TYPES.map(dt => (
              <DocUploadCard
                key={dt.value}
                docType={dt.value}
                label={dt.label}
                icon={dt.icon}
                ucc={ucc}
                onExtracted={handleOcrExtracted}
                existingDocs={documents}
              />
            ))}
          </div>
        </div>
      )}

      {/* Manual Fields */}
      <h3 className="text-sm font-semibold text-slate-700 mb-4 uppercase tracking-wider">Document Details</h3>
      {(onFile.passportNumber || onFile.panCard || onFile.aadharCard) && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mb-4" data-testid="pii-edit-hint">
          For security, Passport / PAN / Aadhaar are hidden. Leave a field blank to keep the value on file; type a new value only to change it.
        </p>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
        <div className="md:col-span-2">
          <Label className="text-sm font-semibold text-slate-700 mb-3 block">Passport Number</Label>
          <Input name="passportNumber" data-testid="input-passport" value={formData.passportNumber} onChange={handleChange} placeholder={onFile.passportNumber ? hiddenHint : 'P1234567'} className="h-12 rounded-xl" />
        </div>

        <div>
          <Label className="text-sm font-semibold text-slate-700 mb-3 block">Passport Issue Date</Label>
          <Input name="passportIssueDate" data-testid="input-passport-issue" type="date" value={formData.passportIssueDate} onChange={handleChange} className="h-12 rounded-xl" />
        </div>

        <div>
          <Label className="text-sm font-semibold text-slate-700 mb-3 block">Passport Expiry Date</Label>
          <Input name="passportExpiryDate" data-testid="input-passport-expiry" type="date" value={formData.passportExpiryDate} onChange={handleChange} className="h-12 rounded-xl" />
        </div>

        <div className="md:col-span-2">
          <Label className="text-sm font-semibold text-slate-700 mb-3 block">Passport Issued At</Label>
          <CityCombobox
            value={formData.passportIssuedAt || ''}
            onChange={(v) => handleSelectChange('passportIssuedAt', v)}
            options={INDIAN_PASSPORT_CITIES}
            placeholder="Type or pick a Passport Seva Kendra city"
            testId="select-passport-city"
          />
        </div>

        <div>
          <Label className="text-sm font-semibold text-slate-700 mb-3 block">PAN Card</Label>
          <Input name="panCard" data-testid="input-pan" value={formData.panCard} onChange={handleChange} placeholder={onFile.panCard ? hiddenHint : 'ABCDE1234F'} className="h-12 rounded-xl" />
        </div>

        <div>
          <Label className="text-sm font-semibold text-slate-700 mb-3 block">Aadhar Card</Label>
          <Input name="aadharCard" data-testid="input-aadhar" value={formData.aadharCard} onChange={handleChange} placeholder={onFile.aadharCard ? hiddenHint : '1234-5678-9012'} className="h-12 rounded-xl" />
        </div>

        <div>
          <Label className="text-sm font-semibold text-slate-700 mb-3 block">Voter ID Number</Label>
          <Input name="voterIdNumber" data-testid="input-voter-id" value={formData.voterIdNumber || formData.visaNumber || ''} onChange={handleChange} placeholder="ABC1234567" className="h-12 rounded-xl" />
        </div>

        <div>
          <Label className="text-sm font-semibold text-slate-700 mb-3 block">Driving License</Label>
          <Input name="licenseNumber" data-testid="input-license" value={formData.licenseNumber || ''} onChange={handleChange} placeholder="DL-1234567890" className="h-12 rounded-xl" />
        </div>
      </div>
    </Card>
  );
};
